<script setup lang="ts">
import { CheckCircle2, ExternalLink, RefreshCw } from 'lucide-vue-next'
import QRCode from 'qrcode'
import { onUnmounted, ref, watch } from 'vue'
import { getRechargeStatus } from '../../api/user/recharge'
import { useI18n } from '../../composables/useI18n'

const props = defineProps<{
  open: boolean
  orderNo: string
  payUrl?: string
  qrCode?: string
  amount?: number
}>()
const emit = defineEmits<{
  'update:open': [value: boolean]
  success: [credit: number]
}>()

const { t } = useI18n()

const QR_SIZE = 220
const POLL_INTERVAL = 2000
const MAX_POLL_TRIES = 300 // 10 minutes

const qrDataUrl = ref('')
const qrLoading = ref(false)
const qrFailed = ref(false)
const paid = ref(false)
const expired = ref(false)
let timer: ReturnType<typeof setInterval> | null = null

/** Connect 返回收银台地址、直连渠道返回二维码内容，两者都编码成二维码供手机扫码 */
function qrPayload() {
  return (props.qrCode || props.payUrl || '').trim()
}

async function renderQr() {
  const payload = qrPayload()
  qrDataUrl.value = ''
  qrFailed.value = false
  if (!payload) {
    qrFailed.value = true
    return
  }
  qrLoading.value = true
  try {
    qrDataUrl.value = await QRCode.toDataURL(payload, {
      width: QR_SIZE * 2,
      margin: 1,
      errorCorrectionLevel: 'M',
    })
  } catch {
    qrFailed.value = true
  } finally {
    qrLoading.value = false
  }
}

function stopPolling() {
  if (timer) {
    clearInterval(timer)
    timer = null
  }
}

function startPolling() {
  stopPolling()
  let tries = 0
  timer = setInterval(async () => {
    tries += 1
    if (tries > MAX_POLL_TRIES) {
      stopPolling()
      expired.value = true
      return
    }
    try {
      const st = await getRechargeStatus(props.orderNo)
      if (st.payed) {
        stopPolling()
        paid.value = true
        emit('success', st.credit)
      }
    } catch {
      // 网络波动时继续轮询
    }
  }, POLL_INTERVAL)
}

async function refresh() {
  expired.value = false
  await renderQr()
  startPolling()
}

watch(
  () => props.open,
  async (open) => {
    stopPolling()
    if (!open) return
    paid.value = false
    expired.value = false
    await renderQr()
    startPolling()
  },
  { immediate: true },
)

function close() {
  emit('update:open', false)
}

function openPayPage() {
  if (props.payUrl) window.open(props.payUrl, '_blank', 'noopener')
}

onUnmounted(stopPolling)
</script>

<template>
  <a-modal
    width="min(420px, 90vw)"
    :open="open"
    :footer="null"
    :title="t('shell.account.recharge.qrTitle')"
    class="min-h-modal"
    @cancel="close"
  >
    <div class="flex flex-col items-center text-center">
      <template v-if="paid">
        <CheckCircle2 class="w-12 h-12 text-green-500" aria-hidden="true" />
        <div class="mt-3 text-base font-semibold text-gray-900 dark:text-gray-100">
          {{ t('shell.account.recharge.qrSuccess') }}
        </div>
      </template>

      <template v-else>
        <div class="text-sm text-gray-500 dark:text-gray-400">
          {{ t('shell.account.recharge.qrHint') }}
        </div>

        <div class="relative mt-4 rounded-xl border border-gray-100 dark:border-gray-800 bg-white p-3">
          <img
            v-if="qrDataUrl"
            :src="qrDataUrl"
            :alt="t('shell.account.recharge.qrTitle')"
            class="w-[220px] h-[220px]"
          />
          <div v-else class="flex w-[220px] h-[220px] items-center justify-center">
            <a-spin v-if="qrLoading" />
            <span v-else class="text-sm text-gray-400">{{ t('shell.account.recharge.qrFailed') }}</span>
          </div>
          <div
            v-if="expired"
            class="absolute inset-0 flex flex-col items-center justify-center gap-2 rounded-xl bg-black/60 text-white"
          >
            <RefreshCw class="w-6 h-6" aria-hidden="true" />
            <span class="text-sm">{{ t('shell.account.recharge.qrExpired') }}</span>
          </div>
        </div>

        <div class="mt-4 text-2xl font-bold text-primary">¥{{ Number(amount || 0).toFixed(2) }}</div>
        <div class="mt-1 text-xs text-gray-400 dark:text-gray-500">
          {{ t('shell.account.recharge.qrOrderNo') }} {{ orderNo }}
        </div>

        <div class="mt-3 flex items-center gap-1 text-sm text-gray-500 dark:text-gray-400">
          <a-spin />
          {{ t('shell.account.recharge.qrWaiting') }}
        </div>

        <div class="mt-4 flex items-center gap-2">
          <a-button v-if="payUrl && !expired" @click="openPayPage">
            <div class="inline-flex items-center gap-1">
              <ExternalLink class="w-4 h-4" aria-hidden="true" />
              {{ t('shell.account.recharge.openPayPage') }}
            </div>
          </a-button>
          <a-button v-if="expired" type="primary" @click="refresh">
            <div class="inline-flex items-center gap-1">
              <RefreshCw class="w-4 h-4" aria-hidden="true" />
              {{ t('shell.account.recharge.qrRefresh') }}
            </div>
          </a-button>
          <a-button v-if="expired || qrFailed" @click="close">{{ t('common.close') }}</a-button>
        </div>
      </template>
    </div>
  </a-modal>
</template>
